import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/common/DataTable/DataTable';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  useEpcs,
  useCreateEpc,
  useUnassignEpc,
  useDeleteEpc,
  useAssignEpc,
  useExportEpcs,
  Epc,
} from '@/api/hooks/useEpcs';
import { useAssetLookup } from '@/api/hooks/useAssets';
import { AsyncSelect } from '@/components/ui/async-select';
import { EpcText } from '@/components/common/EpcText';
import { toast } from 'sonner';
import {
  Search,
  Upload,
  Download,
  Filter,
  Radio,
  Link as LinkIcon,
  Link2Off,
  MoreHorizontal,
  Trash2,
  X,
  Plus,
} from 'lucide-react';

export default function EpcsPage() {
  const { t } = useTranslation(['epcs', 'common']);
  const navigate = useNavigate();

  // Status label helper
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Asignado': return t('status.assigned');
      case 'Disponible': return t('status.available');
      case 'Inactivo': return t('status.inactive');
      default: return status;
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Asignado': return 'bg-blue-100 text-blue-700 hover:bg-blue-200';
      case 'Disponible': return 'bg-green-100 text-green-700 hover:bg-green-200';
      case 'Inactivo': return 'bg-gray-200 text-gray-700 hover:bg-gray-300';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Dialog state
  const [deleteEpc, setDeleteEpc] = useState<Epc | null>(null);
  const [assignEpc, setAssignEpc] = useState<Epc | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string>('');
  const [assetSearch, setAssetSearch] = useState('');
  const [selectedAssetLabel, setSelectedAssetLabel] = useState('');
  const [showNewEpcDialog, setShowNewEpcDialog] = useState(false);
  const [newEpcCode, setNewEpcCode] = useState('');

  // Fetch EPCs from API
  const {
    data: epcsResponse,
    isLoading,
    error,
  } = useEpcs({
    search: searchTerm || undefined,
    status: statusFilter || undefined,
    page,
    pageSize,
  });

  // Fetch assets for assignment (server-side search, only without EPC)
  const { data: assetLookupResults, isLoading: assetLookupLoading } = useAssetLookup(assetSearch, true);

  // Mutations
  const createMutation = useCreateEpc();
  const unassignMutation = useUnassignEpc();
  const deleteMutation = useDeleteEpc();
  const assignMutation = useAssignEpc();
  const exportMutation = useExportEpcs();

  // Stats from API response (global counts, not page-specific)
  const stats = useMemo(() => {
    const data = epcsResponse as (typeof epcsResponse & { assignedCount?: number; availableCount?: number; inactiveCount?: number }) | undefined;
    return {
      total: data?.totalCount ?? 0,
      assigned: data?.assignedCount ?? 0,
      available: data?.availableCount ?? 0,
      inactive: data?.inactiveCount ?? 0,
    };
  }, [epcsResponse]);

  // Handle unassign
  const handleUnassign = async (epc: Epc) => {
    try {
      await unassignMutation.mutateAsync(epc.id);
      toast.success(t('messages.unassigned'));
    } catch {
      toast.error(t('messages.errorUnassign'));
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteEpc) return;
    try {
      await deleteMutation.mutateAsync(deleteEpc.id);
      setDeleteEpc(null);
      toast.success(t('messages.deleted'));
    } catch {
      toast.error(t('messages.errorDelete'));
    }
  };

  // Handle assign
  const handleAssign = async () => {
    if (!assignEpc || !selectedAssetId) return;
    try {
      await assignMutation.mutateAsync({
        epcTag: assignEpc.id,
        assetId: selectedAssetId,
      });
      setAssignEpc(null);
      setSelectedAssetId('');
      setAssetSearch('');
      setSelectedAssetLabel('');
      toast.success(t('messages.assigned'));
    } catch {
      toast.error(t('messages.errorAssign'));
    }
  };

  // Handle export to Excel
  const handleExport = async () => {
    try {
      const blob = await exportMutation.mutateAsync({
        search: searchTerm || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `EPCs_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t('messages.exported'));
    } catch {
      toast.error(t('messages.errorExport'));
    }
  };

  // Handle create new EPC
  const handleCreateEpc = async () => {
    if (!newEpcCode.trim()) return;
    try {
      await createMutation.mutateAsync({ code: newEpcCode.trim() });
      setShowNewEpcDialog(false);
      setNewEpcCode('');
      toast.success(t('messages.created'));
    } catch {
      toast.error(t('messages.errorCreate'));
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || statusFilter;

  // Table columns
  const columns = useMemo<ColumnDef<Epc>[]>(
    () => [
      {
        accessorKey: 'code',
        header: t('table.epcCode'),
        cell: ({ row }) => <EpcText value={row.original.code} className="text-sm" />,
      },
      {
        accessorKey: 'status',
        header: t('table.status'),
        cell: ({ row }) => (
          <Badge className={getStatusBadgeClass(row.original.status)}>
            {getStatusLabel(row.original.status)}
          </Badge>
        ),
      },
      {
        accessorKey: 'assetCode',
        header: t('table.assetCode'),
        cell: ({ row }) =>
          row.original.assetCode ? (
            <Link
              to={`/assets/${row.original.assetId}`}
              className="text-primary hover:underline"
            >
              {row.original.assetCode}
            </Link>
          ) : (
            <span className="text-muted-foreground">-</span>
          ),
      },
      {
        accessorKey: 'assetName',
        header: t('table.assetName'),
        cell: ({ row }) => row.original.assetName || '-',
      },
      {
        id: 'actions',
        header: t('table.actions'),
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">{t('table.actions')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {row.original.status === 'Disponible' ? (
                <DropdownMenuItem onClick={() => setAssignEpc(row.original)}>
                  <LinkIcon className="mr-2 h-4 w-4" />
                  {t('actions.assignToAsset')}
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={() => handleUnassign(row.original)}
                  disabled={unassignMutation.isPending}
                >
                  <Link2Off className="mr-2 h-4 w-4" />
                  {t('actions.unassign')}
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteEpc(row.original)}
                className="text-destructive focus:text-destructive"
                disabled={row.original.status === 'Asignado'}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [t, unassignMutation.isPending]
  );

  // Pagination handlers
  const handlePageChange = (newPage: number) => setPage(newPage);
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pageTitle')}</h1>
          <p className="text-muted-foreground">{t('pageDescription')}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowNewEpcDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('actions.newEpc')}
          </Button>
          <Button variant="outline" onClick={() => navigate('/epcs/import')}>
            <Upload className="mr-2 h-4 w-4" />
            {t('actions.import')}
          </Button>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={exportMutation.isPending}
          >
            <Download className="mr-2 h-4 w-4" />
            {exportMutation.isPending ? t('common:status.processing') : t('actions.export')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.total')}</CardTitle>
            <Radio className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.total}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.assigned')}</CardTitle>
            <LinkIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.assigned}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.available')}</CardTitle>
            <Radio className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.available}</div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t('stats.inactive')}</CardTitle>
            <Radio className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">{stats.inactive}</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* EPC List */}
      <Card>
        <CardHeader>
          <CardTitle>{t('cardTitle')}</CardTitle>
          <CardDescription>{t('cardDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search and Filter Controls */}
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  className="pl-8"
                />
              </div>
              <Button
                variant={showFilters ? 'secondary' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="mr-2 h-4 w-4" />
                {t('common:buttons.filters')}
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2">
                    {t('common:status.active')}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="flex flex-wrap items-center gap-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm font-medium mb-1.5 block">{t('filters.status')}</label>
                  <Select
                    value={statusFilter || 'all'}
                    onValueChange={(v) => {
                      setStatusFilter(v === 'all' ? '' : v);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('status.all')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('status.all')}</SelectItem>
                      <SelectItem value="Disponible">{t('status.available')}</SelectItem>
                      <SelectItem value="Asignado">{t('status.assigned')}</SelectItem>
                      <SelectItem value="Inactivo">{t('status.inactive')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="mr-2 h-4 w-4" />
                    {t('filters.clearFilters')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Data Table */}
          {error ? (
            <div className="text-center py-8 text-destructive">
              {t('messages.errorLoading')}
            </div>
          ) : isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={epcsResponse?.items ?? []}
              pageCount={epcsResponse?.totalPages ?? 1}
              pageIndex={page - 1}
              pageSize={pageSize}
              onPageChange={(pageIndex) => handlePageChange(pageIndex + 1)}
              onPageSizeChange={handlePageSizeChange}
              totalItems={epcsResponse?.totalCount ?? 0}
            />
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteEpc}
        onOpenChange={(open) => !open && setDeleteEpc(null)}
        title={t('dialog.deleteTitle')}
        description={t('dialog.deleteConfirm', { code: deleteEpc?.code })}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Assign Dialog */}
      <Dialog open={!!assignEpc} onOpenChange={(open) => { if (!open) { setAssignEpc(null); setSelectedAssetId(''); setAssetSearch(''); setSelectedAssetLabel(''); } }}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('dialog.assignTitle')}</DialogTitle>
            <DialogDescription>
              {t('dialog.assignDescription')} <span className="font-mono">{assignEpc?.code}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">{t('dialog.assignSelectLabel')}</label>
            <AsyncSelect
              value={selectedAssetId}
              onValueChange={(val) => {
                setSelectedAssetId(val);
                const item = assetLookupResults?.find((a) => a.id === val);
                const fullLabel = item ? `${item.text} - ${item.description || ''}` : '';
                setSelectedAssetLabel(fullLabel.length > 60 ? fullLabel.slice(0, 60) + '...' : fullLabel);
              }}
              options={(assetLookupResults || []).map((a) => ({
                value: a.id,
                label: `${a.text} - ${a.description || ''}`,
                sublabel: a.location || undefined,
              }))}
              isLoading={assetLookupLoading}
              placeholder={t('dialog.assignSelectPlaceholder')}
              searchPlaceholder={t('common:search.placeholder')}
              noResultsText={t('common:messages.noResults')}
              minCharactersText={t('common:search.minCharacters')}
              minCharacters={2}
              searchValue={assetSearch}
              onSearchChange={setAssetSearch}
              selectedLabel={selectedAssetLabel || undefined}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignEpc(null)}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleAssign}
              disabled={!selectedAssetId || assignMutation.isPending}
            >
              {assignMutation.isPending ? t('common:status.processing') : t('common:buttons.assign')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New EPC Dialog */}
      <Dialog open={showNewEpcDialog} onOpenChange={setShowNewEpcDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('dialog.newEpcTitle')}</DialogTitle>
            <DialogDescription>{t('dialog.newEpcDescription')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">{t('dialog.epcCodeLabel')}</label>
            <Input
              value={newEpcCode}
              onChange={(e) => setNewEpcCode(e.target.value)}
              placeholder={t('dialog.epcCodePlaceholder')}
              className="font-mono"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNewEpcDialog(false); setNewEpcCode(''); }}>
              {t('common:buttons.cancel')}
            </Button>
            <Button
              onClick={handleCreateEpc}
              disabled={!newEpcCode.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? t('common:status.saving') : t('common:buttons.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
